/**
 * PlayerMatchReportsSectionView — the match-reports section of a player's page
 * (SE-6986, Figma frame 26).
 *
 * An organism: it renders the `PlayerReportRow` molecule.
 *
 * **The empty state is a sentence, not a blank.** "No match reports yet" is a
 * scouting fact a lead acts on — it means nobody has watched this player under
 * the new format — and an empty region reads as a rendering failure instead.
 *
 * **Each average states the coverage it rests on (SE-7237).** The means skip
 * unscored reads by design, so a card can otherwise present a hard number drawn
 * from a handful of the evaluations on file — Potential is blank for most reads
 * in practice, which made "Avg potential 6.5" look like the settled view of a
 * career it had barely sampled. Disclosing the denominator is preferred over
 * suppressing the number: a sparse average is still worth reading once the
 * reader can see how sparse it is. Never present a number as more certain than
 * it is.
 * @module features/player-match-reports/components/organisms/PlayerMatchReportsSection/PlayerMatchReportsSectionView
 */
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_MATCH_REPORTS } from "@/e2e/testId";

import PlayerReportRow from "../../molecules/PlayerReportRow";

import type {
  PlayerReportRow as PlayerReportRowModel,
  PlayerReportStats,
} from "../../../utils/toPlayerReportRows";

/**
 * The coverage line under one average, or null when there is none to draw.
 *
 * Zero contributors means the card already reads "—", and a denominator under
 * an em dash would claim a measurement that was never taken. Rendered even at
 * full coverage ("from 23 of 23 evaluations"), because a caption that appears
 * only when the news is bad makes its absence a second thing the reader has to
 * know how to interpret.
 * @param from - Reads that carried this score.
 * @param of - Reads that exist at all.
 * @returns The caption text, or null when nothing was scored.
 */
const toCoverageLabel = (from: number, of: number): string | null =>
  from === 0
    ? null
    : `from ${from} of ${of} ${of === 1 ? "evaluation" : "evaluations"}`;

/** Props for PlayerMatchReportsSectionView. */
export interface PlayerMatchReportsSectionViewProps {
  /** One row per fixture, newest first. */
  readonly rows: readonly PlayerReportRowModel[];
  /** The aggregation cards (AC2). */
  readonly stats: PlayerReportStats;
  /** Which rows are expanded, by match id. */
  readonly expandedMatchIds: readonly string[];
  /** Expands or collapses a row. */
  readonly onToggleRow: (matchId: string) => void;
  /** Opens a full match report. */
  readonly onOpenReport: (reportId: string) => void;
  /** True while loading. */
  readonly isLoading: boolean;
  /** A failure, or null. */
  readonly error: string | null;
  /** True when more reports exist than one page carried. */
  readonly hasMore: boolean;
  /**
   * Whether the single-player scout reports BELOW this header have any rows.
   *
   * The "Scout reports" divider belongs to that list, and a screenshot showed
   * it dangling over nothing for a player with no single-player reports —
   * a heading over emptiness reads as a rendering failure, not as a fact.
   */
  readonly hasScoutReports: boolean;
  /**
   * Whose page this is (SE-8016).
   *
   * Named in the expanded panel's scope callout, which says out loud that the
   * rest of the report is not shown here. Optional because the standalone
   * `/players/{id}/reports` route can mount before the player's name resolves;
   * the callout falls back to "this player's" rather than to a blank.
   */
  readonly playerName?: string;
  /**
   * Opens the pick-pipeline-and-stage dialog for this player (SE-7214).
   *
   * Absent when there is no player to promote, in which case the trigger is
   * not rendered at all — a button that cannot act is worse than no button.
   */
  readonly onPromote?: () => void;
}

/**
 * Renders the match-reports section.
 * @param root0 - Component props
 * @param root0.rows - The fixture rows
 * @param root0.stats - The aggregation cards
 * @param root0.expandedMatchIds - Which rows are expanded
 * @param root0.onToggleRow - Expands or collapses a row
 * @param root0.onOpenReport - Opens a full match report
 * @param root0.isLoading - Whether it is loading
 * @param root0.error - A failure to report
 * @param root0.hasMore - Whether the page overflowed
 * @param root0.hasScoutReports - Whether the list below has rows
 * @param root0.playerName - Whose page this is
 * @param root0.onPromote - Opens the add-to-pipeline dialog
 * @returns The section element.
 */
const PlayerMatchReportsSectionView = ({
  rows,
  stats,
  expandedMatchIds,
  onToggleRow,
  onOpenReport,
  isLoading,
  error,
  hasMore,
  hasScoutReports,
  playerName,
  onPromote,
}: PlayerMatchReportsSectionViewProps) => (
  <Stack
    space="3"
    testID={TEST_ID_PLAYER_MATCH_REPORTS.SECTION}
    /*
      The house card, verbatim (SE-8119). Every neighbour on the Reports &
      Notes tab — the AI summary, Scouting Reports, Notes — carries exactly
      this string, so it is copied rather than approximated: a section with no
      frame reads as page chrome instead of as one of the page's components,
      and the eye has nothing telling it where one section ends.

      The box wraps the HEADING too, not just the list, which is what the
      reference frame draws.

      `p-4` replaces the old `pb-4`: the padding is now the card's on every
      side, and keeping a second bottom rule would double it.
    */
    className="rounded-lg border border-outline-card bg-surface-card p-4"
  >
    {/* SE-7214: the title line carries the promote trigger. The button only
        OPENS the existing pick-pipeline-and-stage dialog — it never moves the
        player itself. A silent fixed-stage move would invent a "deep dive"
        stage the boards do not have and mutate real pipeline data without the
        manager confirming a destination, so the board and stage stay a choice.
        The trigger is unconditional by design: no fit%, no "meets criteria"
        banner and no minimum-matches gate stand between a manager and a player
        they want to look at harder. */}
    <Stack
      direction="horizontal"
      space="3"
      className="items-center justify-between"
    >
      {/* The count rides on the section heading rather than on the list card's
          own header, where the reference block puts it: the heading already
          says "Match reports" and repeating the phrase one row below reads as
          a rendering fault rather than as two headings. */}
      <Text variant="title-sm" className="text-content-primary">
        Match reports{rows.length === 0 ? "" : ` (${rows.length})`}
      </Text>

      {onPromote ? (
        <Button
          testID={TEST_ID_PLAYER_MATCH_REPORTS.PROMOTE}
          onPress={onPromote}
          label="Promote to deep-dive"
          tone="primary"
          variant="outline"
          size="sm"
        />
      ) : null}
    </Stack>

    <Stack direction="horizontal" space="3" className="flex-wrap">
      <Box className="min-w-40 flex-1 rounded-lg border border-outline-default bg-surface-raised p-4">
        <Stack space="micro">
          <Text variant="caption" className="text-content-secondary">
            Matches watched
          </Text>
          <Text
            variant="display-sm"
            testID={TEST_ID_PLAYER_MATCH_REPORTS.MATCHES_WATCHED}
            className="text-content-primary"
          >
            {stats.matchesWatched}
          </Text>
        </Stack>
      </Box>

      <Box className="min-w-40 flex-1 rounded-lg border border-outline-default bg-surface-raised p-4">
        <Stack space="micro">
          <Text variant="caption" className="text-content-secondary">
            Avg performance
          </Text>
          {/* An em dash, never a zero: no average exists until someone scores,
              and 0 would read as "rated worthless". */}
          <Text
            variant="display-sm"
            testID={TEST_ID_PLAYER_MATCH_REPORTS.AVG_PERFORMANCE}
            className="text-content-primary"
          >
            {stats.averagePerformance ?? "—"}
          </Text>
          {toCoverageLabel(stats.performanceFrom, stats.evaluationCount) ===
          null ? null : (
            <Text
              variant="micro"
              testID={TEST_ID_PLAYER_MATCH_REPORTS.PERFORMANCE_COVERAGE}
              className="text-content-subtle"
            >
              {toCoverageLabel(stats.performanceFrom, stats.evaluationCount)}
            </Text>
          )}
        </Stack>
      </Box>

      <Box className="min-w-40 flex-1 rounded-lg border border-outline-default bg-surface-raised p-4">
        <Stack space="micro">
          <Text variant="caption" className="text-content-secondary">
            Avg potential
          </Text>
          <Text
            variant="display-sm"
            testID={TEST_ID_PLAYER_MATCH_REPORTS.AVG_POTENTIAL}
            className="text-content-primary"
          >
            {stats.averagePotential ?? "—"}
          </Text>
          {toCoverageLabel(stats.potentialFrom, stats.evaluationCount) ===
          null ? null : (
            <Text
              variant="micro"
              testID={TEST_ID_PLAYER_MATCH_REPORTS.POTENTIAL_COVERAGE}
              className="text-content-subtle"
            >
              {toCoverageLabel(stats.potentialFrom, stats.evaluationCount)}
            </Text>
          )}
        </Stack>
      </Box>
    </Stack>

    {error === null ? null : (
      <Box className="rounded-lg border border-status-error p-4">
        <Text variant="caption" className="text-status-error">
          {error}
        </Text>
      </Box>
    )}

    {isLoading ? (
      <Box className="items-center p-4">
        <Spinner />
      </Box>
    ) : null}

    {/*
      SE-8016: the fixture rows are ONE card with hairline rules between them,
      not a stack of separate cards. A columnar row only reads as a column when
      the cells above and below it line up under an unbroken rule — boxed rows
      with gaps between them are a list of cards that happen to share a width.
      The row molecule owns its own `border-t`, so the card needs no divider of
      its own and the first rule sits under the header.
    */}
    {rows.length === 0 ? null : (
      <Box
        testID={TEST_ID_PLAYER_MATCH_REPORTS.LIST}
        className="overflow-hidden rounded-lg border border-outline-subtle bg-surface-base"
      >
        {/*
          New copy (SE-8016). The section shows the FIRST scout's written
          report and lists the others alongside; a reader with no way to know
          that rule reads the panel as the only report that exists.
        */}
        <Box className="px-4 py-3">
          <Text
            variant="caption"
            testID={TEST_ID_PLAYER_MATCH_REPORTS.OWNERSHIP_NOTE}
            className="text-content-muted"
          >
            Written report ownership: first scout to evaluate
          </Text>
        </Box>

        {rows.map(row => (
          <PlayerReportRow
            key={row.matchId}
            row={row}
            playerName={playerName ?? null}
            isExpanded={expandedMatchIds.includes(row.matchId)}
            onToggleRow={onToggleRow}
            onOpenReport={onOpenReport}
            testID={TEST_ID_PLAYER_MATCH_REPORTS.rowFor(row.matchId)}
          />
        ))}
      </Box>
    )}

    {!isLoading && error === null && rows.length === 0 ? (
      <Box className="rounded-lg border border-outline-default p-4">
        <Text variant="caption" className="text-content-secondary">
          No match reports yet. When a scout notes this player in quick capture,
          the report appears here.
        </Text>
      </Box>
    ) : null}

    {hasMore ? (
      // Transient: the hook pages the career to completion, and this line
      // only shows while older pages are still arriving — numbers above it
      // are not yet totals, and saying so beats letting them read as final.
      <Text variant="micro" className="text-content-subtle">
        Loading older reports — the numbers above still settle.
      </Text>
    ) : null}

    {hasScoutReports ? (
      <Text variant="title-sm" className="pt-2 text-content-primary">
        Scout reports
      </Text>
    ) : null}
  </Stack>
);

PlayerMatchReportsSectionView.displayName = "PlayerMatchReportsSectionView";

export default memo(PlayerMatchReportsSectionView);
